import { useCallback } from 'react';
import chatService from '../services/chatService';
import { useChatSearchStore } from '../store/chatSearchStore';
import { parseSearchQuery } from '../utils/chatSearchQuery';
import { toSearchParams } from '../utils/chatSearchParams';
import { tokenizeQuery } from '../utils/chatSearchHighlight';

/**
 * Runs chat searches against GET /chat/channels/:id/search (PLAN §5.1).
 *
 * Enter runs a search (D3) — never each keystroke: every request may decrypt
 * up to 5,000 rows server-side and is limited to 30/min. A body-dependent
 * search can come back short of `limit` but not complete (the server hit its
 * scan cap); the runner then continues on its own up to AUTO_FOLLOW_UPS more
 * requests, after which "Search older messages" (loadMore) is manual.
 *
 * The request state is module-level so the search bar (run) and the results
 * panel (sort, loadMore) drive the same search. Only the newest request may
 * write results: a newer run, a sort change or a channel switch supersedes
 * (and aborts) the older one.
 */

export const SEARCH_LIMIT = 25;
export const AUTO_FOLLOW_UPS = 3;

let current = { token: 0, controller: null, channelId: null, params: null };

export function cancelChatSearch() {
  current.controller?.abort();
  current = { token: current.token + 1, controller: null, channelId: null, params: null };
}

const ERROR_KEYS = {
  403: 'chat.search.forbidden', // no longer a member
  404: 'chat.search.notFound', // the conversation is gone
  422: 'chat.search.invalid', // a filter the server refused
  429: 'chat.search.rateLimited',
};

function errorFor(err) {
  return { key: ERROR_KEYS[err?.response?.status] || 'chat.search.error' };
}

async function fetchChain(cursor, append) {
  const store = useChatSearchStore.getState;
  const { channelId, params } = current;
  const token = current.token;
  let followUps = 0;
  let nextCursor = cursor;
  let first = true;
  for (;;) {
    const controller = new AbortController();
    current.controller = controller;
    const query = { ...params, sort: store().sort, limit: SEARCH_LIMIT };
    if (nextCursor != null) query.cursor = nextCursor;
    let page;
    try {
      // eslint-disable-next-line no-await-in-loop
      page = await chatService.searchMessages(channelId, query, { signal: controller.signal });
    } catch (err) {
      if (token !== current.token) return;
      store().setError(errorFor(err));
      return;
    }
    if (token !== current.token) return;
    store().applyPage(page, { append: append || !first });
    first = false;
    const s = store();
    if (s.complete || s.nextCursor == null || s.results.length >= SEARCH_LIMIT) return;
    if (followUps >= AUTO_FOLLOW_UPS) return;
    followUps += 1;
    store().setFollowUps(followUps);
    nextCursor = s.nextCursor;
  }
}

/** Parse `input`, build params and run a fresh search in `channelId`. */
export async function runChatSearch(channelId, members, input) {
  const store = useChatSearchStore.getState();
  const parsed = parseSearchQuery(input);
  const tokens = tokenizeQuery(parsed.text);
  cancelChatSearch();
  if (tokens.error) {
    store.openFor(channelId);
    store.setError({ key: 'chat.search.tooLong' });
    return;
  }
  const built = toSearchParams(parsed, members);
  if (built.blocked === 'empty') {
    store.close();
    return;
  }
  store.openFor(channelId);
  if (built.blocked) {
    store.setError({ key: `chat.search.${built.blocked}`, value: built.blockedValue });
    return;
  }
  current = { ...current, channelId, params: built.params };
  store.startSearch(tokens.terms);
  await fetchChain(null, false);
}

/** "Search older messages": continue from the last cursor. */
export async function loadMoreChatSearch() {
  const s = useChatSearchStore.getState();
  if (!current.params || s.complete || s.nextCursor == null || s.status === 'loading') return;
  current = { ...current, token: current.token + 1 };
  useChatSearchStore.setState({ status: 'loading', followUps: 0 });
  await fetchChain(s.nextCursor, true);
}

/** Change the sort and re-run the same search from the top. */
export async function resortChatSearch(sort) {
  const store = useChatSearchStore.getState();
  if (store.sort === sort) return;
  store.setSort(sort);
  if (!current.params) return;
  const { channelId, params } = current;
  const terms = store.terms;
  cancelChatSearch();
  current = { ...current, channelId, params };
  store.startSearch(terms);
  await fetchChain(null, false);
}

export default function useChatSearch(channel) {
  const channelId = channel?.id ?? null;
  const members = channel?.members;
  const run = useCallback((input) => runChatSearch(channelId, members, input), [channelId, members]);
  return { run, loadMore: loadMoreChatSearch, setSort: resortChatSearch, cancel: cancelChatSearch };
}
